import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import WelcomeStep from './components/WelcomeStep';
import InfoStep from './components/InfoStep';
import UpsellStep from './components/UpsellStep';
import VideoStep from './components/VideoStep';
// AmazonModal removed - review step skipped
import AdminDashboard from './components/AdminDashboard';
import AdminConfig from './components/AdminConfig';
import DownloadPage from './components/DownloadPage';
import {
  createSubmission,
  trackGiftsClaimed
} from './utils/supabase';
import { fetchProductConfig } from './utils/config';
import { sendEbookEmail } from './utils/email';

/**
 * CustomerFlow Component - Multi-Product
 * Dynamic step count based on product upsells:
 * 1=Welcome, 2=Info, 3...(2+N)=Upsells, (3+N)=Video
 */
function CustomerFlow() {
  const { productSlug } = useParams();
  const slug = productSlug || 'goat-stand';

  const [currentStep, setCurrentStep] = useState(1);
  const [customerData, setCustomerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    optInSurveys: false
  });
  const [submissionId, setSubmissionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [productConfig, setProductConfig] = useState(null);

  // Email sending refs
  const emailTimerRef = useRef(null);
  const emailSentRef = useRef(false);
  const submissionIdRef = useRef(null);

  useEffect(() => {
    fetchProductConfig(slug).then(config => {
      setProductConfig(config);
    });
  }, [slug]);

  // Cleanup email timer on unmount
  useEffect(() => {
    return () => {
      if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    };
  }, []);

  const upsells = productConfig?.upsells || [];
  const upsellCount = upsells.length;
  const videoStep = 3 + upsellCount; // Video is after all upsells (no review step)

  // Step 1 → Step 2
  const handleWelcomeContinue = () => {
    setCurrentStep(2);
  };

  // Send ebook email via Edge Function (idempotent - safe to call multiple times)
  const triggerEbookEmail = () => {
    if (emailSentRef.current) return;
    emailSentRef.current = true;
    if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    const id = submissionIdRef.current;
    if (!id) return;
    sendEbookEmail(id, slug)
      .catch(err => console.error('Email send error:', err));
  };

  // Step 2 → Step 3 (first upsell or video directly)
  const handleInfoContinue = async (formData) => {
    setIsLoading(true);
    setCustomerData(formData);

    try {
      const id = await createSubmission(formData, slug);
      setSubmissionId(id);
      submissionIdRef.current = id;
      localStorage.setItem('submissionId', id);

      // Start 1m30s fallback timer for email (in case user doesn't reach video step)
      emailSentRef.current = false;
      emailTimerRef.current = setTimeout(() => {
        triggerEbookEmail();
      }, 90 * 1000);

      setCurrentStep(3);
    } catch (error) {
      alert('Error saving your information. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Skip directly to video page
  const handleSkipToGifts = () => {
    setCurrentStep(videoStep);
  };

  // Upsell N → next step
  const handleUpsellContinue = () => {
    setCurrentStep(prev => prev + 1);
  };

  // Back navigation
  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  // Video page viewed - send ebook email immediately
  const handleGiftsClaimed = async () => {
    if (submissionId) {
      try {
        await trackGiftsClaimed(submissionId);
      } catch (error) {
        console.error('Error tracking gifts claimed:', error);
      }
      // Send ebook email now (cancels 1m30s timer if still pending)
      triggerEbookEmail();
    }
  };

  // Determine which upsell index we're on (step 3 = upsell 0, step 4 = upsell 1, etc.)
  const currentUpsellIndex = currentStep - 3;
  const isUpsellStep = currentStep >= 3 && currentStep < videoStep;
  const isVideoStep = currentStep >= videoStep;

  return (
    <div className="min-h-screen bg-goatzy-bg text-goatzy-dark">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
          <div className="text-center">
            <svg
              className="animate-spin h-12 w-12 text-goatzy mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-lg font-light text-goatzy-dark">Loading...</p>
          </div>
        </div>
      )}

      {/* Step 1: Welcome */}
      {currentStep === 1 && (
        <WelcomeStep
          onContinue={handleWelcomeContinue}
          onSkipToGifts={handleSkipToGifts}
          config={productConfig?.welcome_text}
        />
      )}

      {/* Step 2: Customer Info */}
      {currentStep === 2 && (
        <InfoStep
          onContinue={handleInfoContinue}
          onBack={handleBack}
          onSkipToGifts={handleSkipToGifts}
          initialData={customerData}
        />
      )}

      {/* Dynamic Upsell Steps */}
      {isUpsellStep && upsells[currentUpsellIndex] && (
        <UpsellStep
          product={upsells[currentUpsellIndex]}
          stepLabel={`Special Offer ${currentUpsellIndex + 1} of ${upsellCount}`}
          onContinue={handleUpsellContinue}
          onBack={handleBack}
          productSlug={slug}
        />
      )}

      {/* Video Step (after all upsells, or directly if no upsells) */}
      {isVideoStep && (
        <VideoStep
          onGiftsClaimed={handleGiftsClaimed}
          onBack={upsellCount > 0 ? handleBack : undefined}
          videoId={productConfig?.video?.youtube_id}
          productName={productConfig?.name}
          manualPdfUrl={productConfig?.manual_pdf_url}
        />
      )}

    </div>
  );
}

/**
 * AdminAuthGate Component
 * Shared authentication wrapper for admin pages
 */
function AdminAuthGate({ children }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

  useEffect(() => {
    if (sessionStorage.getItem('goatzy_admin_auth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('goatzy_admin_auth', 'true');
    } else {
      setError('Incorrect password');
    }
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  if (isAuthenticated) {
    return children;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-goatzy-bg">
      <div className="max-w-md w-full bg-white p-8 border border-goatzy-pale rounded-xl shadow-sm">
        <h2 className="text-3xl font-light tracking-wide text-center mb-8 text-goatzy-dark">
          Admin Login
        </h2>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-light tracking-wide mb-2 text-gray-700">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-goatzy transition-colors"
              required
            />
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          </div>

          <button
            type="submit"
            className="w-full px-8 py-4 bg-goatzy-dark text-white text-lg font-light tracking-wide rounded-lg
                     hover:bg-goatzy transition-all duration-300
                     focus:outline-none focus:ring-2 focus:ring-goatzy-accent focus:ring-offset-2"
          >
            Login
          </button>

          <button
            type="button"
            onClick={handleBackToMain}
            className="w-full px-8 py-3 border border-goatzy-pale bg-white text-goatzy-dark text-sm font-light tracking-wide rounded-lg
                     hover:border-goatzy transition-all duration-300
                     focus:outline-none focus:ring-2 focus:ring-goatzy-accent focus:ring-offset-2"
          >
            Back to Main
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * DirectVideoPage Component
 * Standalone video page accessible via /:productSlug/gifts
 */
function DirectVideoPage() {
  const { productSlug } = useParams();
  const slug = productSlug || 'goat-stand';
  const [videoId, setVideoId] = useState(null);

  useEffect(() => {
    fetchProductConfig(slug).then(config => {
      if (config?.video?.youtube_id) setVideoId(config.video.youtube_id);
    });
  }, [slug]);

  return (
    <div className="min-h-screen bg-goatzy-bg text-goatzy-dark">
      <VideoStep videoId={videoId} />
    </div>
  );
}

/**
 * Main App Component with Multi-Product Routing
 */
function App() {
  const navigate = useNavigate();

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/goat-stand" replace />} />
      <Route path="/admin" element={
        <AdminAuthGate>
          <AdminDashboard />
        </AdminAuthGate>
      } />
      <Route path="/admin/config" element={
        <AdminAuthGate>
          <AdminConfig onBack={() => navigate('/admin')} />
        </AdminAuthGate>
      } />
      <Route path="/download/:customerId" element={<DownloadPage />} />
      <Route path="/:productSlug/gifts" element={<DirectVideoPage />} />
      <Route path="/:productSlug" element={<CustomerFlow />} />
    </Routes>
  );
}

export default App;
